import {
  addDoc,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  updateDoc,
  where,
  type CollectionReference,
  type DocumentData,
  type DocumentReference,
  type QueryConstraint,
  type Transaction,
  type UpdateData,
  type WithFieldValue,
} from 'firebase/firestore'

/**
 * FirestoreのCRUDを薄く共通化するヘルパ。
 * - パスは `refs` に集約し、このファイルは「編集」だけを担当する
 * - `created_at/updated_at/is_deleted` は base helper で統一
 */

export function createBaseFields(isDeleted = false) {
  return {
    created_at: serverTimestamp(),
    updated_at: serverTimestamp(),
    is_deleted: isDeleted,
  } as const
}

function updateBaseFields() {
  return {
    updated_at: serverTimestamp(),
  } as const
}

/**
 * トランザクション内で「新規ドキュメント追加」する（createBaseFields を付与して transaction.set）。
 * @returns 作成したドキュメントの DocumentReference（ref.id で ID 取得可能）
 */
export function transactionSetWithBaseFields<TDoc extends DocumentData>(
  transaction: Transaction,
  collectionRef: CollectionReference<TDoc, TDoc>,
  payload: WithFieldValue<TDoc>,
  options?: { isDeleted?: boolean },
): DocumentReference<TDoc, TDoc> {
  const ref = doc(collectionRef) as DocumentReference<TDoc, TDoc>
  const mergedPayload = {
    ...payload,
    ...createBaseFields(options?.isDeleted ?? false),
  } as WithFieldValue<TDoc>
  transaction.set(ref, mergedPayload)
  return ref
}

/**
 * トランザクション内で「更新」（updateBaseFields を付与して transaction.update）。
 */
export function transactionUpdateWithUpdatedAt<TDoc extends DocumentData & { updated_at: unknown }>(
  transaction: Transaction,
  docRef: DocumentReference<TDoc, TDoc>,
  patch: UpdateData<TDoc>,
): void {
  const payload: UpdateData<TDoc> = {
    ...patch,
    ...(updateBaseFields() as unknown as UpdateData<TDoc>),
  }
  transaction.update(docRef, payload)
}

export async function getAll<TDoc extends DocumentData, TEntity>(
  collectionRef: CollectionReference<TDoc, TDoc>,
  toEntity: (id: string, doc: TDoc, path: string) => TEntity,
  options?: {
    includeDeleted?: boolean
    constraints?: QueryConstraint[]
  },
): Promise<TEntity[]> {
  const includeDeleted = options?.includeDeleted ?? false
  const constraints = options?.constraints ?? []

  const q = includeDeleted
    ? query(collectionRef, ...constraints)
    : query(collectionRef, where('is_deleted', '==', false), ...constraints)

  const snapshot = await getDocs(q)
  return snapshot.docs.map((snap) => toEntity(snap.id, snap.data(), snap.ref.path))
}

export async function getOne<TDoc extends DocumentData, TEntity>(
  docRef: DocumentReference<TDoc, TDoc>,
  toEntity: (id: string, doc: TDoc, path: string) => TEntity,
  options?: { includeDeleted?: boolean },
): Promise<TEntity | null> {
  const includeDeleted = options?.includeDeleted ?? false
  const snap = await getDoc(docRef)
  if (!snap.exists()) return null
  const data = snap.data()
  if (!includeDeleted && (data as { is_deleted?: boolean })?.is_deleted) return null
  return toEntity(snap.id, data, snap.ref.path)
}

export async function createDoc<TDoc extends DocumentData>(
  collectionRef: CollectionReference<TDoc, TDoc>,
  payload: WithFieldValue<TDoc>,
  options?: { isDeleted?: boolean },
): Promise<string> {
  const mergedPayload = {
    ...payload,
    ...createBaseFields(options?.isDeleted ?? false),
  } as WithFieldValue<TDoc>
  const ref = await addDoc(collectionRef, mergedPayload)
  return ref.id
}

export async function updateWithUpdatedAt<TDoc extends DocumentData & { updated_at: unknown }>(
  docRef: DocumentReference<TDoc, TDoc>,
  patch: UpdateData<TDoc>,
): Promise<void> {
  const payload: UpdateData<TDoc> = {
    ...patch,
    ...(updateBaseFields() as unknown as UpdateData<TDoc>),
  }
  await updateDoc(docRef, payload)
}

export async function softDelete<
  TDoc extends DocumentData & { is_deleted: boolean; updated_at: unknown },
>(
  docRef: DocumentReference<TDoc, TDoc>,
): Promise<void> {
  const payload: UpdateData<TDoc> = {
    is_deleted: true,
    ...(updateBaseFields() as unknown as UpdateData<TDoc>),
  }
  await updateDoc(docRef, payload)
}

